import type { ReactNode } from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { spacing } from '@/theme';

import { onboardingColors } from '@/components/atoms/onboarding/tokens';

interface MarkdownBoldTextProps {
  readonly text: string;
}

const MarkdownBoldText = ({ text }: MarkdownBoldTextProps) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);

  return (
    <>
      {parts.map((part, index) => {
        const isBold = index % 2 === 1;

        return (
          <Text key={index} style={isBold ? styles.boldText : undefined}>
            {part}
          </Text>
        );
      })}
    </>
  );
};

export const ONBOARDING_MAX_ILLUSTRATION_SIZE = 480;
export const ONBOARDING_MAX_CONTENT_WIDTH = 560;

interface OnboardingStepContentProps {
  readonly illustration: (width: number, height: number) => ReactNode;
  readonly title: string;
  readonly body: string;
  readonly stepIndicator?: ReactNode;
  readonly illustrationBackgroundColor?: string;
  readonly illustrationTopOffset?: number;
}

export const OnboardingStepContent = ({
  illustration,
  title,
  body,
  stepIndicator,
  illustrationBackgroundColor,
  illustrationTopOffset,
}: OnboardingStepContentProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const illustrationSize = Math.min(screenWidth, ONBOARDING_MAX_ILLUSTRATION_SIZE);

  const illustrationStyle = [
    styles.illustrationArea,
    illustrationBackgroundColor ? { backgroundColor: illustrationBackgroundColor } : null,
    illustrationTopOffset ? { marginTop: illustrationTopOffset } : null,
  ];

  return (
    <View style={styles.container}>
      <View style={illustrationStyle} testID="onboarding-illustration-area">
        {illustration(illustrationSize, illustrationSize)}
      </View>
      {stepIndicator ? <View style={styles.stepIndicatorContainer}>{stepIndicator}</View> : null}
      <View style={styles.textColumn} testID="onboarding-text-column">
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.body}>
          <MarkdownBoldText text={body} />
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {},
  illustrationArea: {
    alignItems: 'center',
    overflow: 'hidden',
    borderBottomLeftRadius: 90,
    borderBottomRightRadius: 90,
    backgroundColor: onboardingColors.background,
  },
  textColumn: {
    width: '100%',
    maxWidth: ONBOARDING_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
  },
  stepIndicatorContainer: {
    alignItems: 'center',
    marginTop: 30,
  },
  title: {
    marginTop: spacing[4],
    paddingHorizontal: spacing[4],
    textAlign: 'center',
    fontSize: 24,
    fontWeight: '700',
    color: onboardingColors.titleText,
  },
  body: {
    marginTop: 9,
    paddingHorizontal: spacing[4],
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 20,
    color: onboardingColors.bodyText,
  },
  boldText: {
    fontWeight: '700',
  },
});
